import { tzOpts } from "@/lib/tz";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  HeartPulse, Signal, Database, KeyRound, Wallet, CalendarClock,
  CheckCircle2, XCircle, RefreshCw, AlertTriangle, Building2, PauseCircle, PlayCircle,
  Cake, Gift, Bell, HardDrive, FolderTree, Trash2,
} from "lucide-react";

function UsageBar({ label, icon: Icon, pct, detail }) {
  const level = pct >= 95 ? "critico" : pct >= 90 ? "alerta" : pct >= 80 ? "atencao" : "ok";
  const barColor = {
    critico: "bg-red-500", alerta: "bg-red-500", atencao: "bg-amber-500", ok: "bg-emerald-500",
  }[level];
  const textColor = {
    critico: "text-red-400", alerta: "text-red-400", atencao: "text-amber-400", ok: "text-emerald-400",
  }[level];
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <span className={`text-sm font-mono-jb font-semibold ${textColor}`}>{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="text-[11px] text-slate-600 mt-1">{detail}</div>
    </div>
  );
}

const ITEMS = [
  { key: "whatsapp", label: "WhatsApp", icon: Signal },
  { key: "mkauth", label: "mk-auth", icon: Database },
  { key: "efi", label: "EFI", icon: KeyRound },
  { key: "openpix", label: "OpenPix", icon: Wallet },
  { key: "scheduler", label: "Agendador", icon: CalendarClock },
];

function StatusCard({ item, check }) {
  const Icon = item.icon;
  const notConfigured = check && !check.configured;
  const ok = check?.ok;
  return (
    <Card
      className={`p-5 border ${
        notConfigured
          ? "bg-slate-900/30 border-slate-800/60"
          : ok
          ? "bg-emerald-500/5 border-emerald-500/30"
          : "bg-red-500/5 border-red-500/30"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${notConfigured ? "text-slate-600" : ok ? "text-emerald-400" : "text-red-400"}`} />
          <span className="text-sm font-medium text-white">{item.label}</span>
        </div>
        {notConfigured ? (
          <span className="text-[10px] uppercase tracking-wider text-slate-600">não configurado</span>
        ) : ok ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        ) : (
          <XCircle className="w-4 h-4 text-red-400" />
        )}
      </div>
      <p className={`text-xs mt-2 ${notConfigured ? "text-slate-600" : ok ? "text-emerald-300/80" : "text-red-300"}`}>
        {check?.message || "—"}
      </p>
    </Card>
  );
}

export default function Health() {
  const { user } = useAuth();
  const isSuper = !!user?.is_superadmin;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unpausing, setUnpausing] = useState(false);
  const [allTenants, setAllTenants] = useState(null);
  const [alertCfg, setAlertCfg] = useState(null);
  const [savingAlert, setSavingAlert] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [infra, setInfra] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/health/status");
      setStatus(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  const loadSchedule = async () => {
    setScheduleLoading(true);
    try {
      const { data } = await api.get("/billing/schedule-preview");
      setSchedule(data);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setScheduleLoading(false);
    }
  };

  const loadAll = async () => {
    try {
      const { data } = await api.get("/admin/health-status/all");
      setAllTenants(data);
    } catch {
      // silencioso - so o superadmin ve essa parte mesmo
    }
  };

  const loadAlertCfg = async () => {
    try {
      const { data } = await api.get("/admin/health-alert");
      setAlertCfg(data);
    } catch {
      // idem
    }
  };

  const loadInfra = async () => {
    try {
      const { data } = await api.get("/admin/system/infra-health");
      setInfra(data);
    } catch {
      // idem - so o superadmin ve essa parte
    }
  };

  const requestCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/system/infra-health/cleanup-now");
      toast.success(data.message);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setCleaning(false);
    }
  };

  useEffect(() => {
    loadStatus();
    loadSchedule();
    if (isSuper) {
      loadAll();
      loadAlertCfg();
      loadInfra();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAlertCfg = async () => {
    setSavingAlert(true);
    try {
      await api.put("/admin/health-alert", { enabled: alertCfg.enabled, template: alertCfg.template });
      toast.success("Configuração de alerta salva");
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSavingAlert(false);
    }
  };

  const unpause = async () => {
    setUnpausing(true);
    try {
      await api.post("/health/unpause");
      toast.success("Envio de mensagens reativado");
      loadStatus();
      if (isSuper) loadAll();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setUnpausing(false);
    }
  };

  const unpauseTenant = async (tenantId) => {
    try {
      await api.post(`/admin/health-status/${tenantId}/unpause`);
      toast.success("Reativado");
      loadAll();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const brokenCount = allTenants ? allTenants.filter((t) => t.healthy === false).length : 0;
  const pausedTenants = allTenants ? allTenants.filter((t) => t.messaging_paused?.active) : [];

  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Diagnóstico</div>
          <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Saúde do Sistema</h1>
          <p className="text-sm text-slate-500 mt-2">
            Status ao vivo das integrações — WhatsApp, mk-auth, EFI, OpenPix e o agendamento automático.
          </p>
        </div>
        <Button data-testid="health-refresh-btn" onClick={loadStatus} disabled={loading} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Verificar agora
        </Button>
      </header>

      {/* -------- Pausa de segurança anti-ban (a própria empresa) -------- */}
      {status?.messaging_paused?.active && (
        <Card className="bg-amber-500/10 border-amber-500/40 p-5" data-testid="messaging-paused-banner">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <PauseCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-amber-300 font-semibold text-sm">Envio de mensagens pausado por segurança</div>
                <p className="text-xs text-amber-200/70 mt-1">{status.messaging_paused.reason}</p>
                {status.messaging_paused.since && (
                  <p className="text-[11px] text-amber-200/50 mt-0.5">
                    Desde {new Date(status.messaging_paused.since).toLocaleString("pt-BR", tzOpts())}
                  </p>
                )}
              </div>
            </div>
            <Button
              data-testid="unpause-btn"
              onClick={unpause}
              disabled={unpausing}
              className="bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold shrink-0"
            >
              <PlayCircle className="w-4 h-4 mr-1.5" /> {unpausing ? "Reativando…" : "Reativar envios"}
            </Button>
          </div>
        </Card>
      )}

      {status && (
        <div className="flex items-center gap-2 text-sm">
          {status.healthy ? (
            <>
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-300 font-medium">Tudo funcionando normalmente</span>
            </>
          ) : (
            <>
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-red-300 font-medium">
                {status.broken.length} integração(ões) com problema: {status.broken.map((k) => ITEMS.find((i) => i.key === k)?.label || k).join(", ")}
              </span>
            </>
          )}
          <span className="text-xs text-slate-600 ml-auto">
            Verificado às {new Date(status.checked_at).toLocaleTimeString("pt-BR", tzOpts())}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="health-cards">
        {ITEMS.map((item) => (
          <StatusCard key={item.key} item={item} check={status?.checks?.[item.key]} />
        ))}
      </div>

      {/* -------- O que será enviado no próximo ciclo automático -------- */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-emerald-400" />
            <h2 className="font-outfit text-xl font-bold text-white">Cobranças agendadas</h2>
          </div>
          {schedule && (
            <span className="text-xs text-slate-500">
              {schedule.scheduler_enabled ? (
                <>Próxima execução: <span className="text-emerald-300 font-mono-jb">{new Date(schedule.next_run_local).toLocaleString("pt-BR", tzOpts())}</span></>
              ) : (
                <span className="text-red-300">Agendamento desligado</span>
              )}
            </span>
          )}
        </div>
        <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-hidden">
          {scheduleLoading && <div className="py-8 text-center text-slate-500 text-sm">Carregando…</div>}
          {!scheduleLoading && schedule && (
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y divide-slate-800/60" data-testid="billing-schedule-grid">
              {schedule.categories.map((cat) => (
                <div key={cat.key} className="p-4">
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 uppercase tracking-wider">
                    {cat.key === "birthday" ? <Cake className="w-3.5 h-3.5" /> : <Gift className="w-3.5 h-3.5" />}
                    {cat.label}
                  </div>
                  <div className={`text-2xl font-outfit font-bold mt-1 ${cat.count > 0 ? "text-white" : "text-slate-700"}`}>
                    {cat.count}
                  </div>
                  <div className="text-[11px] text-slate-600">cliente(s)</div>
                </div>
              ))}
            </div>
          )}
          {!scheduleLoading && schedule && (
            <div className="px-4 py-3 border-t border-slate-800 text-xs text-slate-500">
              Total: <span className="text-emerald-300 font-semibold">{schedule.total}</span> mensagem(ns) hoje ·
              cobrança em lote fica fora daqui, é disparada sob demanda na tela própria
            </div>
          )}
        </Card>
      </div>

      {isSuper && infra?.available && (
        <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-5" data-testid="infra-health-card">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div className="flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-emerald-400" />
              <div className="font-outfit text-white font-semibold">Disco da VPS</div>
            </div>
            <span className="text-xs text-slate-500">
              checado às {infra.checked_at ? new Date(infra.checked_at).toLocaleTimeString("pt-BR", tzOpts()) : "—"} · a cada 5 min
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <UsageBar
              label="Espaço em disco" icon={HardDrive} pct={infra.disk.use_pct}
              detail={`${infra.disk.used_gb} GB usados de ${infra.disk.total_gb} GB (${infra.disk.free_gb} GB livres)`}
            />
            <UsageBar
              label="Inodes (nº de arquivos)" icon={FolderTree} pct={infra.inodes.use_pct}
              detail={`${infra.inodes.used.toLocaleString("pt-BR", tzOpts())} de ${infra.inodes.total.toLocaleString("pt-BR", tzOpts())} (${infra.inodes.free.toLocaleString("pt-BR", tzOpts())} livres)`}
            />
          </div>

          <p className="text-[11px] text-slate-600 leading-relaxed">
            A cada 5 minutos, um script no próprio servidor confere o uso real do disco. Se passar de 90% (disco ou
            inodes), ele limpa sozinho o cache de build do Docker (nunca imagens ou containers em uso — só o que já
            não serve mais pra nada). Se mesmo assim continuar crítico, você recebe um aviso automático no WhatsApp.
          </p>

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-slate-500">
              {infra.last_cleanup_at
                ? `Última limpeza: ${new Date(infra.last_cleanup_at).toLocaleString("pt-BR", tzOpts())} (liberou ${infra.last_cleanup_reclaimed || "0B"})`
                : "Nenhuma limpeza automática ainda"}
              {infra.cleanup_requested && <span className="text-amber-400 ml-2">· limpeza pedida, aguardando o próximo ciclo</span>}
            </span>
            <Button
              data-testid="infra-cleanup-btn"
              variant="outline"
              onClick={requestCleanup}
              disabled={cleaning || infra.cleanup_requested}
              className="border-slate-700 hover:bg-slate-800 hover:text-white text-xs"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {cleaning ? "Pedindo…" : "Limpar cache agora"}
            </Button>
          </div>
        </Card>
      )}

      {isSuper && allTenants && (
        <>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-emerald-400" />
                <h2 className="font-outfit text-xl font-bold text-white">Todas as empresas</h2>
              </div>
              <span className="text-xs text-slate-500">
                {brokenCount > 0 ? `${brokenCount} com problema` : "todas saudáveis"}
                {pausedTenants.length > 0 && ` · ${pausedTenants.length} pausada(s)`} · atualizado pelo job a cada 10 min
              </span>
            </div>
            <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                  <tr>
                    <th className="text-left py-3 px-4">Empresa</th>
                    {ITEMS.map((item) => (
                      <th key={item.key} className="text-center py-3 px-3">{item.label}</th>
                    ))}
                    <th className="text-center py-3 px-3">Envios</th>
                    <th className="text-left py-3 px-4">Última checagem</th>
                  </tr>
                </thead>
                <tbody data-testid="health-all-tbody">
                  {allTenants.map((t) => (
                    <tr key={t.tenant_id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                      <td className="py-2.5 px-4 text-white">{t.tenant_name}</td>
                      {ITEMS.map((item) => {
                        const c = t.checks?.[item.key];
                        return (
                          <td key={item.key} className="py-2.5 px-3 text-center" title={c?.message || ""}>
                            {!c ? (
                              <span className="text-slate-700">—</span>
                            ) : !c.configured ? (
                              <span className="text-slate-700">·</span>
                            ) : c.ok ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400 inline" />
                            )}
                          </td>
                        );
                      })}
                      <td className="py-2.5 px-3 text-center">
                        {t.messaging_paused?.active ? (
                          <button
                            data-testid={`unpause-tenant-${t.tenant_id}`}
                            onClick={() => unpauseTenant(t.tenant_id)}
                            title={t.messaging_paused.reason}
                            className="inline-flex"
                          >
                            <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 cursor-pointer hover:bg-amber-500/25">
                              Pausado · reativar
                            </Badge>
                          </button>
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-xs text-slate-500 font-mono-jb">
                        {t.checked_at ? new Date(t.checked_at).toLocaleString("pt-BR", tzOpts()) : "ainda não checado"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          {alertCfg && (
            <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4" data-testid="health-alert-card">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <div className="flex items-center gap-2">
                  <HeartPulse className="w-4 h-4 text-emerald-400" />
                  <div className="font-outfit text-white font-semibold">Alerta automático no WhatsApp</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{alertCfg.enabled ? "Ativo" : "Desativado"}</span>
                  <Switch
                    data-testid="health-alert-enabled-switch"
                    checked={!!alertCfg.enabled}
                    onCheckedChange={(v) => setAlertCfg((c) => ({ ...c, enabled: v }))}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Quando uma integração de qualquer empresa cair (deixar de estar OK), o contato dela recebe esse aviso
                automaticamente pelo WhatsApp — só uma vez na queda, não fica repetindo enquanto o problema persiste.
              </p>
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-[0.15em]">Texto do alerta</Label>
                <p className="text-[11px] text-slate-600 mb-1">
                  Variáveis: <span className="font-mono-jb text-emerald-300">{"{empresa} {item} {mensagem}"}</span>
                </p>
                <Textarea
                  data-testid="health-alert-template-input"
                  value={alertCfg.template || ""}
                  onChange={(e) => setAlertCfg((c) => ({ ...c, template: e.target.value }))}
                  rows={6}
                  className="bg-slate-950 border-slate-800 text-slate-200 font-mono-jb text-xs"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setAlertCfg((c) => ({ ...c, template: c.default_template }))}
                  className="border-slate-700 hover:bg-slate-800 hover:text-white"
                >
                  Restaurar padrão
                </Button>
                <Button data-testid="health-alert-save-btn" onClick={saveAlertCfg} disabled={savingAlert} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                  Salvar
                </Button>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
